import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBatteryTests } from '../api/client';
import type { BatteryTestResult } from '../api/client';
import { Badge } from '@connectdevice/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@connectdevice/ui';
import { Button } from '@connectdevice/ui';

const STATUS_VARIANT: Record<BatteryTestResult['status'], 'success' | 'warning' | 'danger' | 'info'> = {
  GOOD: 'success',
  HALF: 'warning',
  DEFECT: 'danger',
  RECARGA: 'info',
};

export function BatteryTestsPage() {
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['battery-tests', page, limit],
    queryFn: () => getBatteryTests(page, limit),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Battery Test Logs</h2>
        <p className="text-sm text-gray-500 mt-1">
          Showing {data?.data.length ?? 0} of {data?.total ?? 0} results
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Test Results</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-gray-400 py-8 text-center">Loading...</p>}
          {isError && <p className="text-red-500 py-8 text-center">Failed to load test results.</p>}

          {data && (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="py-3 pr-4">Serial #</th>
                    <th className="py-3 pr-4">Equipment</th>
                    <th className="py-3 pr-4">Measured mAh</th>
                    <th className="py-3 pr-4">Config mAh</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((test) => (
                    <tr key={test.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 pr-4 font-mono text-xs">{test.serialNumber}</td>
                      <td className="py-3 pr-4">{test.equipmentId}</td>
                      <td className="py-3 pr-4">{test.measuredMah}</td>
                      <td className="py-3 pr-4">{test.configuredMah}</td>
                      <td className="py-3 pr-4">
                        <Badge variant={STATUS_VARIANT[test.status]}>{test.status}</Badge>
                      </td>
                      <td className="py-3 text-gray-500">
                        {new Date(test.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ── Pagination ── */}
              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-500">Page {page}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data.hasNextPage}
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
